import { useEffect, useCallback } from 'react'
import { useTaskStore, Task, TaskStatus } from '@/store/taskStore'
import { tasksApi } from '@/services/api'
import { wsService, WebSocketMessage } from '@/services/websocket'

export function useTasks() {
  const {
    tasks,
    isLoading,
    error,
    setTasks,
    addTask,
    updateTask,
    setLoading,
    setError,
  } = useTaskStore()

  // Handle incoming WebSocket task updates
  useEffect(() => {
    const unsubscribeCreated = wsService.on('task_created', (message: WebSocketMessage) => {
      const taskData = message.data as {
        id: string
        title: string
        description?: string
        status: TaskStatus
        progress: number
      }

      addTask({
        id: taskData.id,
        title: taskData.title,
        description: taskData.description,
        status: taskData.status || 'pending',
        progress: taskData.progress || 0,
        subTasks: [],
        createdAt: new Date(message.timestamp),
        updatedAt: new Date(message.timestamp),
      })
    })

    const unsubscribeUpdate = wsService.on('task_update', (message: WebSocketMessage) => {
      const data = message.data as {
        task_id: string
        status?: TaskStatus
        progress?: number
        message?: string
      }

      updateTask(data.task_id, {
        status: data.status,
        progress: data.progress,
      })
    })

    const unsubscribeCompleted = wsService.on('task_completed', (message: WebSocketMessage) => {
      const data = message.data as { task_id: string }

      updateTask(data.task_id, {
        status: 'completed',
        progress: 1,
      })
    })

    const unsubscribeFailed = wsService.on('task_failed', (message: WebSocketMessage) => {
      const data = message.data as { task_id: string; error?: string }

      updateTask(data.task_id, {
        status: 'failed',
        errorMessage: data.error,
      })
    })

    const unsubscribeKillSwitch = wsService.on('kill_switch_activated', () => {
      // Mark all running tasks as cancelled
      tasks
        .filter((t) => t.status === 'running' || t.status === 'pending')
        .forEach((t) => {
          updateTask(t.id, { status: 'cancelled' })
        })
    })

    return () => {
      unsubscribeCreated()
      unsubscribeUpdate()
      unsubscribeCompleted()
      unsubscribeFailed()
      unsubscribeKillSwitch()
    }
  }, [addTask, updateTask, tasks])

  const loadTasks = useCallback(async () => {
    setLoading(true)
    try {
      const data = await tasksApi.getTasks()
      const mappedTasks: Task[] = data.map((t: {
        id: string
        title: string
        description?: string
        status: TaskStatus
        progress: number
        error_message?: string
        sub_tasks?: { id: string; title: string; status: TaskStatus }[]
        created_at: string
        updated_at: string
      }) => ({
        id: t.id,
        title: t.title,
        description: t.description,
        status: t.status,
        progress: t.progress,
        errorMessage: t.error_message,
        subTasks: t.sub_tasks || [],
        createdAt: new Date(t.created_at),
        updatedAt: new Date(t.updated_at),
      }))
      setTasks(mappedTasks)
    } catch (err) {
      setError('Failed to load tasks')
      console.error('Failed to load tasks:', err)
    } finally {
      setLoading(false)
    }
  }, [setTasks, setLoading, setError])

  const cancelTask = useCallback(
    async (taskId: string) => {
      try {
        await tasksApi.cancelTask(taskId)
        updateTask(taskId, { status: 'cancelled' })
      } catch (err) {
        setError('Failed to cancel task')
        console.error('Failed to cancel task:', err)
      }
    },
    [updateTask, setError]
  )

  const runningTasks = tasks.filter((t) => t.status === 'running')
  const pendingTasks = tasks.filter((t) => t.status === 'pending')
  const completedTasks = tasks.filter((t) => t.status === 'completed')
  const failedTasks = tasks.filter((t) => t.status === 'failed')

  return {
    tasks,
    runningTasks,
    pendingTasks,
    completedTasks,
    failedTasks,
    isLoading,
    error,
    loadTasks,
    cancelTask,
    setError,
  }
}
